import produce, { original } from 'immer'
import { find, get, keyBy, map, set, size, some, values } from 'lodash'

import {
  TAdditionalService,
  TOrderEntryBackendService,
  TOrderEntryService,
} from './types'

export function getRawAdditionalServices(): {
  label: string
  level: 'shipment' | 'coli'
  name: string
  values: any[]
}[] {
  return [
    {
      label: i18n.t('cod', 'Nachnahme'),
      level: 'shipment',
      name: 'COD',
      values: [
        {
          type: 'currency',
          label: i18n.t('cod', 'Nachnahme'),
          name: 'amount',
          defaultValue: { currency: 'CHF', value: 0 },
        },
      ],
    },
    {
      label: i18n.t('hazardous_goods_service', 'Gefahrgutservice'),
      level: 'coli',
      name: 'GGS',
      values: [
        { type: 'hazmat', label: i18n.t('hazardous_good', 'Gefahrgut') },
      ],
    },
    {
      label: i18n.t('signature', 'Unterschrift'),
      level: 'coli',
      name: 'UNT',
      values: [
        {
          type: 'bool',
          label: i18n.t('signature', 'Unterschrift'),
          name: 'signature',
          defaultValue: false,
        },
      ],
    },
    {
      label: i18n.t('bulky_goods', 'Sperrgut'),
      level: 'coli',
      name: 'SGZ',
      values: [
        {
          type: 'bool',
          label: i18n.t('bulky_goods', 'Sperrgut'),
          name: 'sgz',
          defaultValue: false,
        },
      ],
    },
    {
      label: i18n.t('insurance', 'Versicherung'),
      level: 'shipment',
      name: 'INS',
      values: [
        {
          type: 'currency',
          label: i18n.t('transport_insurance', 'Transportversicherung'),
          name: 'amount',
          defaultValue: { currency: 'EUR', value: 0 },
        },
      ],
    },
    {
      label: i18n.t(
        'delivery_confirmation_through_driver',
        'Zustellbestätigung durch Fahrer',
      ),
      level: 'shipment',
      name: 'DDC',
      values: [
        {
          type: 'bool',
          label: i18n.t(
            'confirmation_receipt_driver',
            'Empfangsbestätigung (Fahrer)',
          ),
          name: 'ddc',
          defaultValue: false,
        },
      ],
    },
    {
      label: i18n.t('driver_instructions', 'Fahreranweisungen'),
      level: 'shipment',
      name: 'DIS',
      values: [
        {
          type: 'input',
          label: i18n.t('driver_instructions', 'Fahreranweisungen'),
          name: 'dir',
          defaultValue: false,
        },
      ],
    },
    {
      label: i18n.t('reusable_box', 'Mehrwegbox'),
      level: 'coli',
      name: 'RBX',
      values: [
        {
          type: 'reusableBox',
          label: i18n.t('reusable_box', 'Mehrwegbox'),
          name: 'rbx',
          defaultValue: false,
        },
      ],
    },
    {
      label: i18n.t('code_delivery', 'Codezustellung'),
      level: 'shipment',
      name: 'CZST',
      values: [
        {
          type: 'input',
          label: i18n.t('code_delivery', 'Codezustellung'),
          name: 'value',
          defaultValue: '',
          placeholder: i18n.t(
            'enter_delivery_code_hier',
            'Zustell Code hier angeben',
          ),
        },
      ],
    },
    {
      label: i18n.t('identity_check', 'Identitätsprüfung'),
      level: 'shipment',
      name: 'IDCK',
      values: [
        {
          type: 'bool',
          label: i18n.t('identity_check', 'Identitätsprüfung'),
          name: 'idck',
          defaultValue: false,
        },
      ],
    },
    {
      label: i18n.t('neighbour_delivery', 'Nachbarzustellung'),
      level: 'shipment',
      name: 'NBZ',
      values: [
        {
          type: 'bool',
          label: i18n.t('neighbour_delivery', 'Nachbarzustellung'),
          name: 'nbz',
          defaultValue: false,
        },
      ],
    },
    {
      label: i18n.t('drop_off_permission', 'Abstellgenehmigung'),
      level: 'shipment',
      name: 'ASG',
      values: [
        {
          type: 'bool',
          label: i18n.t('drop_off_permission', 'Abstellgenehmigung'),
          name: 'asg',
          defaultValue: false,
        },
      ],
    },
    {
      label: i18n.t(
        'mailbox_delivery_allowed',
        'Briefkastenzustellung erlaubt',
      ),
      level: 'shipment',
      name: 'BKZ',
      values: [
        {
          type: 'bool',
          label: i18n.t(
            'mailbox_delivery_allowed',
            'Briefkastenzustellung erlaubt',
          ),
          name: 'bkz',
          defaultValue: false,
        },
      ],
    },
    {
      label: i18n.t('pickup_hint_instruction', 'Abholhinweis'),
      level: 'shipment',
      name: 'DIP',
      values: [
        {
          type: 'input',
          label: i18n.t('pickup_hint_instruction', 'Abholhinweis'),
          name: 'dip',
          defaultValue: '',
        },
      ],
    },
    {
      label: i18n.t('delivery_hint_instruction', 'Zustellhinweis'),
      level: 'shipment',
      name: 'DID',
      values: [
        {
          type: 'input',
          label: i18n.t('delivery_hint_instruction', 'Zustellhinweis'),
          name: 'did',
          defaultValue: '',
        },
      ],
    },
    {
      label: i18n.t('phone_avis_pickup', 'telefonische Avis Abholung'),
      level: 'shipment',
      name: 'PPA',
      values: [
        {
          type: 'input',
          label: i18n.t('phone_avis_pickup', 'telefonische Avis Abholung'),
          name: 'ppa',
          defaultValue: '',
        },
      ],
    },
    {
      label: i18n.t('phone_avis_delivery', 'telefonische Avis Zustellung'),
      level: 'shipment',
      name: 'PPA',
      values: [
        {
          type: 'input',
          label: i18n.t('phone_avis_delivery', 'telefonische Avis Zustellung'),
          name: 'ppa',
          defaultValue: '',
        },
      ],
    },
  ]
}

export const ADDITIONAL_SERVICES: {
  [name in TAdditionalService]?: TOrderEntryService
} = keyBy(
  backendServicesToServices(
    getRawAdditionalServices().filter((service) =>
      some(
        values(get(global, 'additionalServices', [])),
        (s: { name: string; description: string }) => s.name === service.name,
      ),
    ) as TOrderEntryBackendService[],
  ),
  'name',
)

/**
 * Clear value label if only one value exists.
 * Set isMainMirror if only one boolean value exists.
 * @param services
 * @returns
 */
export function backendServicesToServices(
  services: TOrderEntryBackendService[],
): TOrderEntryService[] {
  const rawAdditionalServices = getRawAdditionalServices()
  return map(
    services,
    produce((draft: TOrderEntryBackendService) => {
      if (size(get(draft, 'values')) === 1) {
        const service = find(rawAdditionalServices, {
          name: get(draft, 'name'),
        })
        set(
          draft,
          'label',
          get(service, 'label') || get(draft, 'label') || get(draft, 'name'),
        )
        set(draft, ['values', 0, 'label'], undefined)
        if (get(original(draft), ['values', 0, 'type']) === 'bool') {
          set(draft, 'isMainMirror', true)
        }
      }
    }),
  )
}
